import { Button } from "@aix/ui"
import { useNavigate, useParams } from "@tanstack/react-router"
import { ArrowLeft, CheckCircle, Circle, Clock, PlayCircle, Video } from "lucide-react"
import toast from "react-hot-toast"
import { trpc } from "../../lib/trpc"

export default function JourneyDetailScreen() {
  const navigate = useNavigate()
  const { journeyId } = useParams({ strict: false }) as { journeyId: string }

  const { data: journey, isLoading } = trpc.videoOnboarding.getJourney.useQuery({ journeyId })
  const { data: progress } = trpc.videoOnboarding.getJourneyProgress.useQuery({ journeyId })

  const enrollMutation = trpc.videoOnboarding.enrollInJourney.useMutation({
    onSuccess: () => {
      toast.success("Successfully enrolled in journey!")
      // Refetch journey to update enrollment status
      void trpc.useUtils().videoOnboarding.getJourney.invalidate({ journeyId })
    },
    onError: (error) => {
      toast.error(error.message || "Failed to enroll in journey")
    },
  })

  const handleEnroll = async () => {
    await enrollMutation.mutateAsync({ journeyId })
  }

  const handleVideoClick = (videoId: string) => {
    navigate({
      to: "/onboarding/video/$videoId",
      params: { videoId },
      // @ts-ignore
      search: { journeyId },
    })
  }

  const findNextVideo = () => {
    if (!journey?.videos) return null
    // Find first unwatched video
    const nextVideo = journey.videos.find((v) => !v.userProgress || v.userProgress.completionPercentage < 90)
    return nextVideo || journey.videos[0] // Or first video if all watched
  }

  const continueWatching = () => {
    const nextVideo = findNextVideo()
    if (nextVideo) {
      handleVideoClick(nextVideo.id)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!journey) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-primary mb-4">Journey not found</h2>
          <Button onClick={() => navigate({ to: "/onboarding/journeys" })}>Back to Journeys</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate({ to: "/onboarding/journeys" })}
          className="flex items-center gap-2 text-muted-foreground hover:text-primary mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Journeys
        </button>

        {/* Journey Header */}
        <div className="bg-secondary rounded-lg p-8 mb-6 border border-border">
          <div className="flex items-start gap-4 mb-4">
            <div className="text-5xl">{journey.icon || "📚"}</div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-primary mb-2">{journey.name}</h1>
              {journey.description && <p className="text-muted-foreground">{journey.description}</p>}
            </div>
          </div>

          {/* Meta Info */}
          <div className="flex items-center gap-6 text-sm text-muted-foreground mb-4">
            <div className="flex items-center gap-2">
              <Video className="w-4 h-4" />
              <span>{journey.videos?.length || 0} videos</span>
            </div>
            {journey.estimatedMinutes && (
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{journey.estimatedMinutes} minutes</span>
              </div>
            )}
          </div>

          {/* Progress Bar */}
          {journey.isEnrolled && progress && (
            <div className="mb-4">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-muted-foreground">
                  Progress: {progress.watchedVideos}/{progress.totalVideos} videos completed
                </span>
                <span className="font-semibold text-primary">{progress.completionPercentage}%</span>
              </div>
              <div className="w-full bg-background rounded-full h-3">
                <div
                  className="bg-primary h-3 rounded-full transition-all"
                  style={{ width: `${progress.completionPercentage}%` }}
                />
              </div>
            </div>
          )}

          {/* Tags */}
          {journey.tags && journey.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {journey.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 bg-background rounded-full text-sm text-muted-foreground">
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            {journey.isEnrolled ? (
              <Button onClick={continueWatching} className="flex-1">
                <PlayCircle className="w-4 h-4 mr-2" />
                {progress && progress.watchedVideos > 0 ? "Continue Learning" : "Start Journey"}
              </Button>
            ) : (
              <Button onClick={handleEnroll} loading={enrollMutation.isLoading} className="flex-1">
                Enroll in Journey
              </Button>
            )}
          </div>
        </div>

        {/* Video List */}
        <div className="space-y-3">
          <h2 className="text-xl font-semibold text-primary mb-4">Videos</h2>
          {journey.videos && journey.videos.length > 0 ? (
            journey.videos.map((video, index) => {
              const isWatched = video.userProgress && video.userProgress.completionPercentage >= 90
              const isInProgress =
                video.userProgress &&
                video.userProgress.completionPercentage > 0 &&
                video.userProgress.completionPercentage < 90

              return (
                <div
                  key={video.id}
                  onClick={() => handleVideoClick(video.id)}
                  className="bg-secondary rounded-lg p-4 border border-border hover:border-primary transition-all cursor-pointer group"
                >
                  <div className="flex items-start gap-4">
                    {/* Order Number & Status Icon */}
                    <div className="flex flex-col items-center gap-2 min-w-[40px]">
                      <span className="text-lg font-semibold text-muted-foreground">{index + 1}</span>
                      {isWatched ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <Circle className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>

                    {/* Thumbnail */}
                    {video.thumbnailUrl && (
                      <img src={video.thumbnailUrl} alt={video.title} className="w-32 h-20 object-cover rounded" />
                    )}

                    {/* Video Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-primary group-hover:text-accent transition-colors mb-1">
                        {video.title}
                      </h3>
                      {video.description && (
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{video.description}</p>
                      )}

                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        {video.duration && (
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            <span>{Math.ceil(video.duration / 60)} min</span>
                          </div>
                        )}
                        {isInProgress && video.userProgress && (
                          <div className="text-primary font-medium">
                            {video.userProgress.completionPercentage}% watched
                          </div>
                        )}
                        {isWatched && <div className="text-green-500 font-medium">Completed</div>}
                      </div>

                      {/* Progress Bar for In-Progress Videos */}
                      {isInProgress && video.userProgress && (
                        <div className="mt-2 w-full bg-background rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full transition-all"
                            style={{ width: `${video.userProgress.completionPercentage}%` }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Play Button */}
                    <div className="flex items-center">
                      <PlayCircle className="w-8 h-8 text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                  </div>
                </div>
              )
            })
          ) : (
            <div className="text-center py-12 bg-secondary rounded-lg">
              <Video className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No videos in this journey yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
